/**
 * ReportContentItemsStep
 *
 * Displays a scrollable list of content report items with a chevron on the right.
 * Used for each drill-down level in the ReportContentSheet stepper.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import type { ContentReportItemEntity } from '@/domain/entities/report/contentReportItemEntity';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { ForwardChevronIcon } from '@/presentation/components/common/ForwardChevronIcon';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface ReportContentItemsStepProps {
  items: ContentReportItemEntity[];
  isLoading: boolean;
  onItemTap: (item: ContentReportItemEntity) => void;
}

export const ReportContentItemsStep: React.FC<ReportContentItemsStepProps> = ({
  items,
  isLoading,
  onItemTap,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          padding: responsive.spacing['3xl'],
        }}
      >
        <ExpauseLoadingIndicator />
      </div>
    );
  }

  return (
    <div
      style={{
        overflowY: 'auto',
        height: '100%',
        paddingLeft: responsive.spacing.xl,
        paddingRight: responsive.spacing.xl,
        paddingTop: responsive.spacing.lg,
        paddingBottom: responsive.spacing.lg,
      }}
    >
      {items.map((item) => (
        <div
          key={item.id}
          {...qaAttr({ id: `report-content-reason-${item.id}` })}
          onClick={() => onItemTap(item)}
          style={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            paddingBottom: responsive.spacing['2xl'],
          }}
        >
          <span
            style={{
              flex: 1,
              color: theme.text.primary,
              fontSize: responsive.font.base,
              lineHeight: 1.4,
            }}
          >
            {item.title}
          </span>
          <ForwardChevronIcon />
        </div>
      ))}
    </div>
  );
};
